import ScrollLockAnimation from './scroll-lock-animation';
import Header from './header/header';

const settings = {
    cssProperties: {
        buttonHeight: '--tab-section-button-height',
    },

    classNames: {
        container: 'tab-section',
        sticky: 'tab-section--sticky',
        shadowed: 'tab-section--shadowed',
        active: 'tab-section--active',
    },
};

export default class TabSection {
    constructor(container) {
        this.container = container;
        this.currentTabIndex = null;
        this.sticky = false;
        this.navScrollTimeout = null;

        // Find our tab buttons and content panels
        this.buttonContainer = this.container.querySelector('.tab-section--buttons');
        this.buttons = this.container.querySelectorAll('.tab-section--buttons > div > button');

        this.panelContainer = this.container.querySelector('.tab-section--content');
        this.panels = this.container.querySelectorAll('.tab-section--content > div');

        if (this.buttons.length != this.panels.length) {
            console.log(`Number of tab buttons doesn't match number of content panels`);
            return;
        }

        for (let i=0; i<this.buttons.length; i++) {
            this.buttons[i].addEventListener(
                'click',
                function() {
                    // Perform navigation
                    this.navigate(i);

                    // Check if the top of our container is above the top of
                    // the viewport
                    const bounds = this.container.getBoundingClientRect();
                    if (bounds.top < 0) {
                        // Set this flag to prevent sticky changes for a moment
                        if (this.navScrollTimeout) {
                            clearTimeout(this.navScrollTimeout);
                        }

                        this.navScrollTimeout = setTimeout(
                            function() {
                                this.navScrollTimeout = null;
                            }.bind(this),
                            1000,
                        );

                        // Also tell the header to remain hidden for a moment
                        Header.suppressVisibility(1000);

                        // Scroll upward
                        window.scrollTo({
                            left: 0,
                            top: window.pageYOffset +
                                bounds.top,
                            behavior: 'smooth',
                        });
                    }
                }.bind(this),
            );
        }

        // Monitor the height of our button container, as this is used to add
        // padding when it becomes sticky
        let buttonContainerHeight = null;
        const resizeCallback = function() {
            if (this.buttonContainer.clientHeight !== buttonContainerHeight) {
                buttonContainerHeight = this.buttonContainer.clientHeight;

                this.container.style.setProperty(
                    settings.cssProperties.buttonHeight,
                    `${buttonContainerHeight}px`,
                );
            }
        }.bind(this);

        window.addEventListener('resize', resizeCallback);
        resizeCallback();

        // Register for scroll events
        const viewportPositions = {
            beforeSticky: 0,
            duringSticky: 1,
            afterSticky: 2,
        };

        let viewportPosition = null;

        ScrollLockAnimation.addScrollPositionChangeCallback(
            function() {
                const bounds = this.container.getBoundingClientRect();
                if (bounds.top < 0) {
                    if (bounds.bottom > buttonContainerHeight) {
                        // We should be sticky (tabs are at top of viewport)
                        if (!this.sticky) {
                            // Check the last recorded viewport position
                            switch (viewportPosition) {
                                case viewportPositions.afterSticky:
                                    // Animate transition
                                    this.setStickyState(true, false);
                                    break;

                                default:
                                    // Transition immediately
                                    this.setStickyState(true, true);
                                    break;
                            }
                        }

                        viewportPosition = viewportPositions.duringSticky;
                    } else {
                        // We shouldn't be sticky (tabs are above viewport)
                        if (this.sticky) {
                            // Make sure we're not scrolling upwards
                            if (this.navScrollTimeout === null) {
                                // Animate transition
                                this.setStickyState(false, false);
                            }
                        }

                        viewportPosition = viewportPositions.afterSticky;
                    }
                } else {
                    // We shouldn't be sticky (tabs are below top of viewport)
                    if (this.sticky) {
                        // Transition immediately
                        this.setStickyState(false, true);
                    }

                    viewportPosition = viewportPositions.beforeSticky;
                }
            }.bind(this),
        );

        // Perform initial navigation
        this.navigate(0, true);
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .getElementsByClassName(settings.classNames.container);

        for (let i=0; i<elements.length; i++) {
            new TabSection(elements[i]);
        }
    }

    navigate(tabIndex, immediate = false) {
        if (
            tabIndex < 0 ||
            tabIndex === this.currentTabIndex ||
            tabIndex >= this.buttons.length
        ) {
            return;
        }

        // Stop any ongoing animation
        if (this.navTimeline) {
            this.navTimeline.progress(1);
        }

        // Set preconditions
        const fromPanel = this.currentTabIndex !== null ?
            this.panels[this.currentTabIndex] :
            null;

        const toPanel = this.panels[tabIndex];

        if (fromPanel) {
            this.buttons[this.currentTabIndex]
                .classList
                .remove(settings.classNames.active);
        }

        this.buttons[tabIndex]
            .classList
            .add(settings.classNames.active);

        toPanel
            .classList
            .add(settings.classNames.active);

        // EDIT: since the layouts of the content are so similar,
        // doing any sort of transition ends up looking kind of
        // cruddy - just forcing immediate transitions for now
        if (immediate || true) {
            // Hide current tab
            if (fromPanel) {
                fromPanel
                    .classList
                    .remove(settings.classNames.active);
            }
        } else {
            // Animate transition
            this.navTimeline = gsap.timeline();

            if (fromPanel) {
                this.buttons[this.currentTabIndex]
                    .classList
                    .remove(settings.classNames.active);

                fromPanel.style.position = 'absolute';

                // Resize container
                this.panelContainer.style.height = `${fromPanel.clientHeight}px`;
                this.navTimeline.to(
                    this.panelContainer,
                    1.0,
                    {
                        ease: 'power2.out',
                        height: toPanel.clientHeight,
                    },
                    0,
                );

                // Fade out current tab
                this.navTimeline.to(
                    fromPanel,
                    0.75,
                    {
                        ease: 'power2.out',
                        opacity: 0,
                    },
                    0,
                );
            }

            // Fade in new tab
            this.navTimeline.from(
                toPanel,
                0.75,
                {
                    ease: 'power2.out',
                    opacity: 0,
                },
                0.25,
            );

            this.navTimeline.add(
                function() {
                    this.navTimeline = null;

                    this.panelContainer.removeAttribute('style');

                    if (fromPanel) {
                        fromPanel.removeAttribute('style');
                        fromPanel
                            .classList
                            .remove(settings.classNames.active);
                    }

                    toPanel.removeAttribute('style');
                }.bind(this),
            );
        }

        // Update our current tab index
        this.currentTabIndex = tabIndex;
    }

    setStickyState(newSticky, immediate = false) {
        if (newSticky == this.sticky) {
            return;
        }

        // Stop any ongoing animation
        if (this.stickyTimeline) {
            this.stickyTimeline.progress(1);
        }

        if (newSticky) {
            // Becoming sticky
            this.sticky = true;

            // Add class to container & buttons
            this.container
                .classList
                .add(settings.classNames.sticky);

            this.buttonContainer
                .classList
                .add(settings.classNames.sticky);

            // Add the shadow on the next tick so that a CSS
            // transition will fire that fades it in
            setTimeout(
                function() {
                    this.buttonContainer
                        .classList
                        .add(settings.classNames.shadowed);
                }.bind(this),
                1,
            );

            // Move the buttons to the end of the DOM
            // so that they're not affected by scroll locking
            document.body.appendChild(this.buttonContainer);

            if (!immediate) {
                // Animate transition
                this.stickyTimeline = gsap.timeline();
                this.stickyTimeline.fromTo(
                    this.buttonContainer,
                    0.375,
                    {
                        yPercent: -100,
                    },
                    {
                        ease: 'power3.out',
                        yPercent: 0,
                    },
                );

                this.stickyTimeline.add(
                    function() {
                        this.stickyTimeline = null;
                        this.buttonContainer.removeAttribute('style');
                    }.bind(this),
                );
            }
        } else {
            // Becoming unsticky
            this.sticky = false;

            const finishTransition = function() {
                // Remove class from container & buttons
                this.container
                    .classList
                    .remove(settings.classNames.sticky);

                this.buttonContainer
                    .classList
                    .remove(settings.classNames.sticky);

                // Move the buttons back to their original location
                // in the DOM
                this.container.insertBefore(
                    this.buttonContainer,
                    this.panelContainer,
                );

                // Remove the shadow on the next tick so that a CSS
                // transition will fire that fades it out
                setTimeout(
                    function() {
                        this.buttonContainer
                            .classList
                            .remove(settings.classNames.shadowed);
                    }.bind(this),
                    1,
                );
            }.bind(this);

            if (immediate) {
                finishTransition();
            } else {
                // Animate transition
                this.stickyTimeline = gsap.timeline();
                this.stickyTimeline.fromTo(
                    this.buttonContainer,
                    0.375,
                    {
                        yPercent: 0,
                    },
                    {
                        ease: 'power3.out',
                        yPercent: -100,
                    },
                );

                this.stickyTimeline.add(
                    function() {
                        this.stickyTimeline = null;
                        finishTransition();
                        this.buttonContainer.removeAttribute('style');
                    }.bind(this),
                );
            }
        }
    }
}
